// AdminLedgerPanel — full-page admin wallet ledger audit (Phase 7.8 follow-up).
//
// 2026-05-17. Routed at /settings/admin-ledger (alongside the other
// admin-only ops tools). Lets the admin browse, filter, and audit
// every row written to admin_wallet_ledger by the double-entry
// middleware (Phase 7.3) — including the cryptographic hash chain
// columns (Phase 7.8) so a tampering attempt is visible.
//
// Backend surface: see shared/src/core/controllers/adminWallet.controller.js
//   GET /api/admin/wallet/kinds          — enum list for the filter dropdown
//   GET /api/admin/wallet/ledger?…       — paginated, filtered, hashes included
//   GET /api/admin/wallet/verify-chain   — runs Invariant #15 on demand
//   GET /api/admin/wallet/balance        — singleton lifetime balance
//
// UX notes:
//   - Filter chips collapse on mobile; pagination uses keyset-style
//     limit/offset so it can scale beyond the 200-row clamp on the
//     model layer.
//   - The hash badge per row shows the first 8 chars of row_hash. A
//     "verify chain" button at the top runs the full O(N) re-walk
//     and renders a green/red status pill.
//   - Amount color follows admin's P&L sign convention (green inflow,
//     red outflow). The kind chip uses category colours (profit /
//     loss / admin / cryptochain).

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { levelName } from '../../lib/levelNames';
import { motion } from 'motion/react';
import Icon from '@baaligames/shared-ui/components/Icon';
import { adminApi } from '@baaligames/shared-ui/admin/lib/adminApi';

// ════════════════════════════════════════════════════════════════════
// Types
// ════════════════════════════════════════════════════════════════════

type LedgerRow = {
  id: number;
  occurred_at: string;
  kind: string;
  amount: number;
  balance_after: number;
  source_table: string | null;
  source_id: number | null;
  source_ref: string | null;
  match_id: string | null;
  game: string | null;
  counterparty_kind: string | null;
  counterparty_id: number | null;
  actor_admin_id: number | null;
  note: string | null;
  idempotency_key: string | null;
  prev_hash: string;
  row_hash: string;
};

type LedgerResp = {
  rows: LedgerRow[];
  total: number;
  limit: number;
  offset: number;
  chainStatus?: ChainStatus;
};

type ChainStatus = {
  invariant?: string;
  ok: boolean;
  deviation?: number;
  rowsChecked?: number;
  sample?: Array<{ id: number; failure: string; hint?: string }>;
  message?: string;
  threw?: boolean;
};

type KindsResp = { kinds: string[]; counterparty_kinds: string[] };

// ════════════════════════════════════════════════════════════════════
// Phase 17.2 — Deposit-liability ledger types
// ════════════════════════════════════════════════════════════════════
//
// admin_deposit_ledger mirrors L1 deposit movements only. Each row
// records that admin's "we owe the agent this much deposit back"
// liability grew or shrank. The controller (Phase 17.2) attaches a
// `agent` summary (id + username + level + parent_username) and a
// structured `entry_type` so the UI can chip-filter without parsing
// source_ref grammar.

type DepositLedgerRow = {
  id: number;
  occurred_at: string;
  kind: 'agent_deposit_received' | 'agent_deposit_consumed' | 'agent_deposit_refunded' | 'correction';
  amount: number;
  liability_after: number;
  agent_id: number | null;
  agent: {
    id: number;
    username: string;
    level: number | null;
    parent_username: string | null;
  } | null;
  source_ledger_id: number | null;
  source_ref: string | null;
  actor_admin_id: number | null;
  note: string | null;
  idempotency_key: string | null;
  entry_type:
    | 'manual_admin' | 'manual_admin_withdraw'
    | 'manual_parent'
    | 'request_recharge' | 'request_withdrawal'
    | 'exit_refund' | 'settlement_burn'
    | 'correction' | 'system';
  actor: 'admin' | 'parent_agent' | 'agent_request' | 'platform' | 'system';
};

type DepositLedgerResp = {
  rows: DepositLedgerRow[];
  total: number;
  limit: number;
  offset: number;
  liability: number;
};

// ════════════════════════════════════════════════════════════════════
// Category quick-filters for P&L kinds (Phase 17.3)
// ════════════════════════════════════════════════════════════════════
//
// Admin asked for clear category chips on top of the long kind row.
// Each chip selects/deselects the bundle of kinds that belongs to it.
// Categories overlap the kind enum but don't replace it — the kind
// row still lets admin drill in to a single enum value.

const PNL_CATEGORIES: Array<{
  id: 'gameplay' | 'promotional' | 'manual' | 'payouts' | 'corrections';
  label: string;
  icon: string;
  tone: 'fuchsia' | 'amber' | 'slate' | 'rose' | 'yellow';
  kinds: string[];
}> = [
  { id: 'gameplay',     label: 'Gameplay (PvP / Bots)', icon: 'sports_esports', tone: 'fuchsia',
    kinds: [
      'platform_fee_admin_share', 'platform_fee_bot_share',
      'win_loss_admin_share_in', 'win_loss_admin_share_out',
      'bot_match_inflow', 'bot_match_outflow',
    ] },
  { id: 'promotional',  label: 'Promotional / Rewards', icon: 'redeem',        tone: 'amber',
    kinds: ['offer_admin_share', 'referral_admin_funded'] },
  { id: 'manual',       label: 'Manual transfers',      icon: 'edit_note',     tone: 'slate',
    kinds: ['manual_credit', 'manual_debit'] },
  { id: 'payouts',      label: 'Payouts / Refunds',     icon: 'payments',      tone: 'rose',
    kinds: [
      'user_withdrawal_paid', 'agent_withdrawal_paid',
      'user_refund_funded', 'bulk_payout_platform_funded',
      'security_deposit_returned',
    ] },
  { id: 'corrections',  label: 'Corrections',           icon: 'tune',          tone: 'yellow',
    kinds: ['correction'] },
];

const CATEGORY_TONE: Record<string, { active: string; inactive: string }> = {
  fuchsia: {
    active:   'bg-fuchsia-600 text-white ring-fuchsia-600 shadow-[0_2px_10px_rgba(217,70,239,0.35)]',
    inactive: 'bg-fuchsia-50 dark:bg-fuchsia-500/15 ring-fuchsia-300/40 dark:ring-fuchsia-400/30 text-fuchsia-800 dark:text-fuchsia-200 hover:bg-fuchsia-100',
  },
  amber: {
    active:   'bg-amber-600 text-white ring-amber-600 shadow-[0_2px_10px_rgba(245,158,11,0.35)]',
    inactive: 'bg-amber-50 dark:bg-amber-500/15 ring-amber-300/40 dark:ring-amber-400/30 text-amber-800 dark:text-amber-200 hover:bg-amber-100',
  },
  slate: {
    active:   'bg-slate-700 text-white ring-slate-700 shadow-[0_2px_10px_rgba(71,85,105,0.35)]',
    inactive: 'bg-slate-100 dark:bg-slate-500/20 ring-slate-300/40 dark:ring-slate-400/30 text-slate-800 dark:text-slate-200 hover:bg-slate-200',
  },
  rose: {
    active:   'bg-rose-600 text-white ring-rose-600 shadow-[0_2px_10px_rgba(244,63,94,0.35)]',
    inactive: 'bg-rose-50 dark:bg-rose-500/15 ring-rose-300/40 dark:ring-rose-400/30 text-rose-800 dark:text-rose-200 hover:bg-rose-100',
  },
  yellow: {
    active:   'bg-yellow-600 text-white ring-yellow-600 shadow-[0_2px_10px_rgba(202,138,4,0.35)]',
    inactive: 'bg-yellow-50 dark:bg-yellow-500/15 ring-yellow-300/40 dark:ring-yellow-400/30 text-yellow-800 dark:text-yellow-200 hover:bg-yellow-100',
  },
};

// ════════════════════════════════════════════════════════════════════
// Helpers — kind classification + amount formatting
// ════════════════════════════════════════════════════════════════════

// Pillar grouping for visual tone — matches business intent, not the
// raw signed amount (so admin can scan "platform fees" rows distinctly
// from "win/loss" rows even if both happen to be inflows in this slice).
const KIND_GROUP: Record<string, 'fee' | 'win_loss' | 'bot' | 'offer' | 'referral' | 'payout' | 'recharge' | 'deposit' | 'manual' | 'correction'> = {
  platform_fee_admin_share:   'fee',
  platform_fee_bot_share:     'fee',
  win_loss_admin_share_in:    'win_loss',
  win_loss_admin_share_out:   'win_loss',
  bot_match_inflow:           'bot',
  bot_match_outflow:          'bot',
  offer_admin_share:          'offer',
  referral_admin_funded:      'referral',
  bulk_payout_platform_funded:'payout',
  user_withdrawal_paid:       'payout',
  agent_withdrawal_paid:      'payout',
  user_refund_funded:         'recharge',
  security_deposit_returned:  'deposit',
  manual_credit:              'manual',
  manual_debit:               'manual',
  correction:                 'correction',
};

const GROUP_TONE: Record<string, { bg: string; ring: string; text: string; label: string }> = {
  fee:        { bg: 'bg-emerald-50 dark:bg-emerald-500/15',  ring: 'ring-emerald-300/40 dark:ring-emerald-400/30',  text: 'text-emerald-800 dark:text-emerald-200',  label: 'Fee' },
  win_loss:   { bg: 'bg-violet-50 dark:bg-violet-500/15',    ring: 'ring-violet-300/40 dark:ring-violet-400/30',    text: 'text-violet-800 dark:text-violet-200',    label: 'Win/Loss' },
  bot:        { bg: 'bg-fuchsia-50 dark:bg-fuchsia-500/15',  ring: 'ring-fuchsia-300/40 dark:ring-fuchsia-400/30',  text: 'text-fuchsia-800 dark:text-fuchsia-200',  label: 'Bot' },
  offer:      { bg: 'bg-amber-50 dark:bg-amber-500/15',      ring: 'ring-amber-300/40 dark:ring-amber-400/30',      text: 'text-amber-800 dark:text-amber-200',      label: 'Offer' },
  referral:   { bg: 'bg-sky-50 dark:bg-sky-500/15',          ring: 'ring-sky-300/40 dark:ring-sky-400/30',          text: 'text-sky-800 dark:text-sky-200',          label: 'Referral' },
  payout:     { bg: 'bg-rose-50 dark:bg-rose-500/15',        ring: 'ring-rose-300/40 dark:ring-rose-400/30',        text: 'text-rose-800 dark:text-rose-200',        label: 'Payout' },
  recharge:   { bg: 'bg-cyan-50 dark:bg-cyan-500/15',        ring: 'ring-cyan-300/40 dark:ring-cyan-400/30',        text: 'text-cyan-800 dark:text-cyan-200',        label: 'Refund' },
  deposit:    { bg: 'bg-indigo-50 dark:bg-indigo-500/15',    ring: 'ring-indigo-300/40 dark:ring-indigo-400/30',    text: 'text-indigo-800 dark:text-indigo-200',    label: 'Deposit' },
  manual:     { bg: 'bg-slate-100 dark:bg-slate-500/20',     ring: 'ring-slate-300/40 dark:ring-slate-400/30',      text: 'text-slate-800 dark:text-slate-200',      label: 'Manual' },
  correction: { bg: 'bg-yellow-50 dark:bg-yellow-500/15',    ring: 'ring-yellow-300/40 dark:ring-yellow-400/30',    text: 'text-yellow-800 dark:text-yellow-200',    label: 'Correction' },
  other:      { bg: 'bg-arena-100 dark:bg-arena-700/30',     ring: 'ring-arena-300/40 dark:ring-arena-600/40',      text: 'text-arena-700 dark:text-neon-200/80',    label: 'Other' },
};

// 2026-05-18 (Phase 17.5 fix): the original Phase 7.8 implementation
// of this helper assumed `*_ledger.amount` columns are stored in
// paise and divided by 100. That assumption was WRONG — every other
// inr() helper in this codebase (40+ across admin/agent/player/
// shared-ui) treats the value as WHOLE RUPEES and does no division.
// Settlement engine, offer engine, recharge requests, withdrawal
// requests, parent↔sub transfers — all store rupees-as-integer.
// The divide-by-100 here was making every row display as 1/100th
// of its real value (user typed ₹1,000, panel showed "₹10.00").
// Caught when a user manually deposited ₹1,000 and saw ₹10.
const inr = (rupees: number) => {
  const abs = Math.abs(rupees);
  const formatted = abs.toLocaleString('en-IN');
  return `${rupees < 0 ? '−' : ''}₹${formatted}`;
};

const fmtWhen = (s: string) => {
  // Trim trailing 'Z' / fractional sec for readability; show in
  // server's local. The exact ISO is available via title attribute.
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  const dd = d.toLocaleString('en-IN', {
    year: 'numeric', month: 'short', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
  return dd;
};

const shortHash = (h: string) => (h && h.length >= 8 ? h.slice(0, 8) : (h || '—'));

// ════════════════════════════════════════════════════════════════════
// AdminLedgerPanel
// ════════════════════════════════════════════════════════════════════

const PAGE = 50;

export default function AdminLedgerPanel() {
  // Phase 17.2 — top-level toggle: Operating Wallet (P&L) vs Security
  // Deposits (Liability). The two views read different tables and
  // need distinct filter sets, so we branch the entire render based
  // on this value. Filters are reset when switching views.
  const [view, setView] = useState<'pnl' | 'deposit'>('pnl');

  // ── Filter state (P&L) ─────────────────────────────────────────
  const [kinds, setKinds] = useState<string[]>([]);
  const [kindList, setKindList] = useState<string[]>([]);
  const [counterpartyKinds, setCounterpartyKinds] = useState<string[]>([]);
  const [selectedCounterpartyKind, setSelectedCounterpartyKind] = useState<string>('');
  const [counterpartyId, setCounterpartyId] = useState<string>('');
  const [from, setFrom] = useState<string>('');
  const [to, setTo] = useState<string>('');
  const [search, setSearch] = useState<string>('');
  const [page, setPage] = useState<number>(0);

  // ── Total row counts per view (Phase 17.4) ────────────────────
  // Both pills carry a live row count so admin can never miss the
  // fact that data exists in the inactive view. Reported repeatedly
  // by users who manually credited a deposit, landed on the default
  // P&L view (empty by design — Phase 17.1 made deposits liability-
  // not-P&L), and assumed the transaction was lost.
  const [pnlTotalCount, setPnlTotalCount] = useState<number | null>(null);
  const [depositTotalCount, setDepositTotalCount] = useState<number | null>(null);

  // ── Filter state (deposit) ─────────────────────────────────────
  const [depositKinds, setDepositKinds] = useState<string[]>([]);
  const [depositKindList, setDepositKindList] = useState<string[]>([]);
  const [depositEntryTypes, setDepositEntryTypes] = useState<string[]>([]);
  const [depositAgentId, setDepositAgentId] = useState<string>('');
  const [depositSearch, setDepositSearch] = useState<string>('');
  const [depositPage, setDepositPage] = useState<number>(0);
  const [depositData, setDepositData] = useState<DepositLedgerResp | null>(null);
  const [depositLoading, setDepositLoading] = useState(true);
  const [depositErr, setDepositErr] = useState<string | null>(null);
  // Liability total (singleton) — shown in the header when deposit view active.
  const [liability, setLiability] = useState<number | null>(null);

  // ── Data state ──────────────────────────────────────────────────
  const [data, setData] = useState<LedgerResp | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [balance, setBalance] = useState<number | null>(null);
  const [chainStatus, setChainStatus] = useState<ChainStatus | null>(null);
  const [verifying, setVerifying] = useState(false);

  // ── Load enum list once ────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const r = await adminApi.get<KindsResp>('/admin/wallet/kinds');
        setKindList(r.kinds);
        setCounterpartyKinds(r.counterparty_kinds);
      } catch (e: any) { setErr(e?.message || String(e)); }
    })();
    (async () => {
      try {
        const r = await adminApi.get<{ balance: number }>('/admin/wallet/balance');
        setBalance(r.balance);
      } catch { /* non-fatal */ }
    })();
    // Phase 17.2 — load deposit enum + initial liability snapshot.
    (async () => {
      try {
        const r = await adminApi.get<{ kinds: string[] }>('/admin/wallet/deposit/kinds');
        setDepositKindList(r.kinds);
      } catch { /* non-fatal — deposit tab still works without chips */ }
    })();
    (async () => {
      try {
        const r = await adminApi.get<{ liability: number }>('/admin/wallet/deposit/liability');
        setLiability(r.liability);
      } catch { /* non-fatal */ }
    })();
  }, []);

  // ── Build query string ──────────────────────────────────────────
  const queryString = useMemo(() => {
    const p = new URLSearchParams();
    p.set('limit', String(PAGE));
    p.set('offset', String(page * PAGE));
    if (kinds.length > 0) p.set('kinds', kinds.join(','));
    if (from) p.set('from', from);
    if (to)   p.set('to',   to);
    if (selectedCounterpartyKind) p.set('counterparty_kind', selectedCounterpartyKind);
    if (counterpartyId.trim()) p.set('counterparty_id', counterpartyId.trim());
    if (search.trim()) p.set('search', search.trim());
    return p.toString();
  }, [kinds, from, to, selectedCounterpartyKind, counterpartyId, search, page]);

  // ── Load page ──────────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const r = await adminApi.get<LedgerResp>(`/admin/wallet/ledger?${queryString}`);
      setData(r);
      // Keep the toggle badge in sync with the underlying table.
      // (When there are NO filters this equals the row total; with
      // filters applied it shows the filtered count — that's also the
      // right signal for the admin: "X rows in your current view".)
      setPnlTotalCount(r.total);
    } catch (e: any) { setErr(e?.message || String(e)); }
    finally { setLoading(false); }
  }, [queryString]);

  useEffect(() => { load(); }, [load]);

  // ── Deposit ledger query string + loader (Phase 17.2) ───────────
  const depositQueryString = useMemo(() => {
    const p = new URLSearchParams();
    p.set('limit', String(PAGE));
    p.set('offset', String(depositPage * PAGE));
    if (depositKinds.length > 0) p.set('kinds', depositKinds.join(','));
    if (depositAgentId.trim()) p.set('agent_id', depositAgentId.trim());
    if (from) p.set('from', from);    // re-use date range
    if (to)   p.set('to',   to);
    if (depositSearch.trim()) p.set('search', depositSearch.trim());
    return p.toString();
  }, [depositKinds, depositAgentId, from, to, depositSearch, depositPage]);

  const loadDeposit = useCallback(async () => {
    setDepositLoading(true); setDepositErr(null);
    try {
      const r = await adminApi.get<DepositLedgerResp>(`/admin/wallet/deposit/ledger?${depositQueryString}`);
      // Client-side entry-type filter — backend returns every row, the
      // chip narrows down. Keep the server query simple; admin won't
      // page-skip with this filter so 50 rows is fine.
      const filtered = depositEntryTypes.length === 0
        ? r.rows
        : r.rows.filter((row) => depositEntryTypes.includes(row.entry_type));
      setDepositData({ ...r, rows: filtered });
      setLiability(r.liability);
      setDepositTotalCount(r.total);
    } catch (e: any) {
      setDepositErr(e?.message || String(e));
    } finally { setDepositLoading(false); }
  }, [depositQueryString, depositEntryTypes]);

  useEffect(() => {
    if (view === 'deposit') loadDeposit();
  }, [view, loadDeposit]);

  // Eagerly fetch the deposit row total whenever the admin is on the
  // P&L view, so the toggle badge always reflects current state. This
  // is cheap (limit=1) and runs only on the P&L tab; the deposit tab
  // already gets its count from loadDeposit() above.
  useEffect(() => {
    if (view !== 'pnl') return;
    let cancelled = false;
    (async () => {
      try {
        const r = await adminApi.get<DepositLedgerResp>('/admin/wallet/deposit/ledger?limit=1');
        if (!cancelled) {
          setDepositTotalCount(r.total);
          setLiability(r.liability);
        }
      } catch { /* non-fatal — badge just stays at its last known value */ }
    })();
    return () => { cancelled = true; };
  }, [view]);

  // ── Verify chain on demand ─────────────────────────────────────
  const verifyChain = useCallback(async () => {
    setVerifying(true);
    try {
      const r = await adminApi.get<ChainStatus>('/admin/wallet/verify-chain');
      setChainStatus(r);
    } catch (e: any) {
      setChainStatus({ ok: false, threw: true, message: e?.message || String(e) });
    } finally { setVerifying(false); }
  }, []);

  // ── Filter helpers ─────────────────────────────────────────────
  const toggleKind = (k: string) => {
    setKinds((prev) => prev.includes(k) ? prev.filter(x => x !== k) : [...prev, k]);
    setPage(0);
  };
  // Phase 17.3 — category quick-filter: toggle a bundle of kinds.
  // If ANY kind in the category is currently selected, treat the chip
  // as "active" and clear all of that category's kinds. Otherwise add
  // every kind in the category to the selection set.
  const toggleCategory = (catId: string) => {
    const cat = PNL_CATEGORIES.find(c => c.id === catId);
    if (!cat) return;
    setKinds((prev) => {
      const anySelected = cat.kinds.some(k => prev.includes(k));
      if (anySelected) return prev.filter(k => !cat.kinds.includes(k));
      const seen = new Set(prev);
      const next = [...prev];
      for (const k of cat.kinds) if (!seen.has(k)) next.push(k);
      return next;
    });
    setPage(0);
  };
  const clearAllFilters = () => {
    setKinds([]); setFrom(''); setTo(''); setSearch('');
    setSelectedCounterpartyKind(''); setCounterpartyId('');
    setPage(0);
  };

  // Phase 17.2 — deposit-view filter helpers.
  const toggleDepositKind = (k: string) => {
    setDepositKinds((prev) => prev.includes(k) ? prev.filter(x => x !== k) : [...prev, k]);
    setDepositPage(0);
  };
  const toggleDepositEntryType = (t: string) => {
    setDepositEntryTypes((prev) => prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t]);
    setDepositPage(0);
  };
  const clearAllDepositFilters = () => {
    setDepositKinds([]); setDepositEntryTypes([]); setDepositAgentId('');
    setDepositSearch(''); setFrom(''); setTo('');
    setDepositPage(0);
  };

  const pages = data ? Math.max(1, Math.ceil(data.total / PAGE)) : 1;

  // ════════════════════════════════════════════════════════════════════
  // Render
  // ════════════════════════════════════════════════════════════════════

  return (
    <div className="space-y-4 max-w-[1400px]">
      {/* Phase 7 — one Audit area: hop to the rate/setting-change twin. */}
      <Link
        to="/?drill=audit"
        className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[12px] font-semibold
                   text-arena-600 dark:text-neon-200/70
                   bg-arena-100/70 dark:bg-white/[0.06]
                   ring-1 ring-inset ring-arena-200/60 dark:ring-white/10
                   hover:bg-arena-100 dark:hover:bg-white/10 transition"
      >
        <Icon name="swap_horiz" size={14} />
        <span>Also in Audit: <span className="font-bold">rate &amp; setting changes</span></span>
      </Link>
      {/* ─── View toggle (Phase 17.2) ──────────────────────────── */}
      {/* The two views read different tables (admin_wallet_ledger vs
          admin_deposit_ledger) and require distinct filter sets. The
          toggle is the very first thing on the page so admin always
          knows which book they're looking at. */}
      {/* Phase 17.4 — REAL toggle. Previous version was a thin pill
          row inside the page chrome that admins simply could not
          see (user feedback: "tab buttons are so small I couldn't
          even see them"). The new design is a full-width 2-column
          segmented control with big tap targets, large icon, primary-
          weight title, secondary subtitle (balance / liability), and
          a row-count + pulse-dot affordance on whichever tab has
          data the admin hasn't visited yet. Impossible to miss. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <button
          type="button"
          onClick={() => setView('pnl')}
          className={`relative text-left rounded-2xl p-4 sm:p-5 ring-2 transition active:scale-[0.99]
            ${view === 'pnl'
              ? 'bg-violet-600 ring-violet-600 text-white shadow-[0_8px_30px_rgba(124,58,237,0.35)]'
              : 'bg-white/70 dark:bg-arena-800/40 ring-arena-200/60 dark:ring-arena-700/40 text-arena-800 dark:text-neon-200/85 hover:ring-violet-300 dark:hover:ring-violet-500/50 hover:bg-violet-50/40 dark:hover:bg-violet-500/10'}`}
        >
          <div className="flex items-start gap-3">
            <span className={`grid place-items-center w-11 h-11 rounded-xl shrink-0
              ${view === 'pnl'
                ? 'bg-white/15 text-white'
                : 'bg-violet-100 text-violet-700 dark:bg-violet-500/20 dark:text-violet-300'}`}>
              <Icon name="account_balance" size={22} filled={view === 'pnl'} />
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <div className={`font-extrabold text-[15px] sm:text-[16px] leading-tight
                  ${view === 'pnl' ? 'text-white' : 'text-neutral-900 dark:text-white'}`}>
                  Operating Wallet
                </div>
                <span className={`inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 rounded-full text-[11px] font-mono font-bold tabular-nums
                  ${view === 'pnl'
                    ? 'bg-white/25 text-white'
                    : 'bg-violet-100 text-violet-800 dark:bg-violet-500/20 dark:text-violet-200'}`}>
                  {pnlTotalCount == null ? '…' : pnlTotalCount}
                </span>
              </div>
              <div className={`text-[11.5px] mt-0.5 leading-snug
                ${view === 'pnl' ? 'text-white/85' : 'text-arena-600 dark:text-neon-200/70'}`}>
                P&L · gameplay, payouts, manual wallet moves
              </div>
              <div className={`text-[12.5px] font-mono font-bold tabular-nums mt-2
                ${view === 'pnl' ? 'text-white' : (
                  balance == null ? 'text-arena-500 dark:text-neon-200/55' :
                  balance > 0 ? 'text-emerald-700 dark:text-emerald-300' :
                  balance < 0 ? 'text-rose-700 dark:text-rose-300' :
                  'text-arena-700 dark:text-neon-200/80'
                )}`}>
                {balance == null ? '—' : inr(balance)}
              </div>
            </div>
          </div>
        </button>
        <button
          type="button"
          onClick={() => setView('deposit')}
          className={`relative text-left rounded-2xl p-4 sm:p-5 ring-2 transition active:scale-[0.99]
            ${view === 'deposit'
              ? 'bg-emerald-600 ring-emerald-600 text-white shadow-[0_8px_30px_rgba(16,185,129,0.35)]'
              : 'bg-white/70 dark:bg-arena-800/40 ring-arena-200/60 dark:ring-arena-700/40 text-arena-800 dark:text-neon-200/85 hover:ring-emerald-300 dark:hover:ring-emerald-500/50 hover:bg-emerald-50/40 dark:hover:bg-emerald-500/10'}`}
        >
          {/* Pulse dot — pulses when P&L is active but deposit has
              data, so the admin sees a strong visual cue that their
              just-completed deposit landed in the OTHER tab. */}
          {view === 'pnl' && (depositTotalCount ?? 0) > 0 && (
            <span className="absolute top-3 right-3 flex h-3 w-3" aria-hidden>
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500"></span>
            </span>
          )}
          <div className="flex items-start gap-3">
            <span className={`grid place-items-center w-11 h-11 rounded-xl shrink-0
              ${view === 'deposit'
                ? 'bg-white/15 text-white'
                : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300'}`}>
              <Icon name="savings" size={22} filled={view === 'deposit'} />
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <div className={`font-extrabold text-[15px] sm:text-[16px] leading-tight
                  ${view === 'deposit' ? 'text-white' : 'text-neutral-900 dark:text-white'}`}>
                  Security Deposits
                </div>
                <span className={`inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 rounded-full text-[11px] font-mono font-bold tabular-nums
                  ${view === 'deposit'
                    ? 'bg-white/25 text-white'
                    : 'bg-emerald-100 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-200'}`}>
                  {depositTotalCount == null ? '…' : depositTotalCount}
                </span>
              </div>
              <div className={`text-[11.5px] mt-0.5 leading-snug
                ${view === 'deposit' ? 'text-white/85' : 'text-arena-600 dark:text-neon-200/70'}`}>
                Liability · manual deposit recharges & withdrawals
              </div>
              <div className={`text-[12.5px] font-mono font-bold tabular-nums mt-2
                ${view === 'deposit' ? 'text-white' : 'text-emerald-700 dark:text-emerald-300'}`}>
                {liability == null ? '—' : inr(liability)}
              </div>
            </div>
          </div>
        </button>
      </div>

      {/* Extra hint when admin is on the P&L tab AND P&L is empty AND
          deposit has rows — points them directly at the right tab so
          they can never lose a deposit transaction again. */}
      {view === 'pnl' && (pnlTotalCount === 0) && (depositTotalCount ?? 0) > 0 && (
        <div className="rounded-xl bg-emerald-50/80 dark:bg-emerald-500/10 ring-1 ring-inset ring-emerald-200/60 dark:ring-emerald-400/30 p-3 text-[12.5px] text-emerald-900 dark:text-emerald-200 flex items-center gap-2">
          <Icon name="lightbulb" size={16} className="shrink-0 text-emerald-700 dark:text-emerald-300" />
          <div className="flex-1">
            <span className="font-semibold">Looking for a deposit recharge / withdrawal?</span>{' '}
            Those don't appear here — they live in the <b>Security Deposits</b> ledger above.
            ({depositTotalCount} row{depositTotalCount === 1 ? '' : 's'} waiting.)
          </div>
          <button
            type="button"
            onClick={() => setView('deposit')}
            className="shrink-0 px-3 py-1 rounded-full text-[11.5px] font-bold bg-emerald-600 text-white hover:bg-emerald-500 active:bg-emerald-700 shadow-[0_2px_8px_rgba(16,185,129,0.35)]"
          >
            Open Security Deposits →
          </button>
        </div>
      )}

      {/* ─── Header card ───────────────────────────────────────── */}
      <div className={`rounded-2xl ring-1 ring-inset p-4 sm:p-5
        ${view === 'pnl'
          ? 'bg-gradient-to-br from-violet-50 to-fuchsia-50 dark:from-violet-950/40 dark:to-fuchsia-950/40 ring-violet-200/60 dark:ring-violet-700/40'
          : 'bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-950/40 dark:to-teal-950/40 ring-emerald-200/60 dark:ring-emerald-700/40'}`}>
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0">
            <div className={`w-10 h-10 rounded-xl text-white grid place-items-center shrink-0
              ${view === 'pnl' ? 'bg-violet-500/90' : 'bg-emerald-500/90'}`}>
              <Icon name={view === 'pnl' ? 'account_balance' : 'savings'} size={22} />
            </div>
            <div className="min-w-0">
              <h2 className="font-bold text-[16px] sm:text-[18px] text-neutral-900 dark:text-white leading-tight">
                {view === 'pnl' ? 'Admin wallet ledger (Operating P&L)' : 'Admin deposit ledger (Liability)'}
              </h2>
              <p className="text-[12px] sm:text-[12.5px] text-arena-700 dark:text-neon-200/85 mt-0.5">
                {view === 'pnl' ? (
                  <>Every double-entry write the platform has made into{' '}
                  <code>admin_wallet_ledger</code>. Cryptographically hashed
                  (Phase 7.8) — see the chain status badge.</>
                ) : (
                  <>Every deposit-liability movement at the L1 boundary, mirrored from{' '}
                  <code>agent_deposit_ledger</code>. Sum of amounts = total
                  collateral the platform currently owes back to L1 agents.</>
                )}
              </p>
            </div>
          </div>
          {/* Balance + chain status pill (P&L) OR Liability total (deposit) */}
          <div className="flex flex-wrap items-center gap-2">
            {view === 'pnl' && (
            <div className="rounded-xl bg-white/70 dark:bg-arena-900/40 ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40 px-3 py-2 text-right">
              <div className="text-[10px] uppercase tracking-wider text-arena-600 dark:text-neon-200/60 font-bold">Lifetime balance</div>
              <div className={`text-[18px] tabular-nums font-extrabold leading-none mt-0.5 ${
                balance == null ? 'text-arena-600 dark:text-neon-200/60' :
                balance > 0 ? 'text-emerald-600 dark:text-emerald-400' :
                balance < 0 ? 'text-rose-600 dark:text-rose-400' :
                'text-arena-700 dark:text-neon-200/80'
              }`}>
                {balance == null ? '—' : inr(balance)}
              </div>
            </div>
            )}
            {view === 'deposit' && (
            <div className="rounded-xl bg-white/70 dark:bg-arena-900/40 ring-1 ring-inset ring-emerald-200/50 dark:ring-emerald-600/40 px-3 py-2 text-right">
              <div className="text-[10px] uppercase tracking-wider text-emerald-700 dark:text-emerald-300 font-bold">Current liability</div>
              <div className="text-[18px] tabular-nums font-extrabold leading-none mt-0.5 text-emerald-700 dark:text-emerald-300">
                {liability == null ? '—' : inr(liability)}
              </div>
              <div className="text-[9.5px] text-emerald-700/70 dark:text-emerald-200/65 leading-none mt-0.5">
                owed back to L1 agents
              </div>
            </div>
            )}
            {view === 'pnl' && (
            <button
              onClick={verifyChain}
              disabled={verifying}
              className={`rounded-xl px-3 py-2 text-[12px] font-semibold inline-flex items-center gap-1.5 ring-1 ring-inset transition disabled:opacity-60 ${
                chainStatus == null
                  ? 'bg-white/70 dark:bg-arena-900/40 ring-arena-200/50 dark:ring-arena-600/40 text-arena-700 dark:text-neon-200/85'
                  : chainStatus.ok
                  ? 'bg-emerald-50 dark:bg-emerald-500/15 ring-emerald-300/50 dark:ring-emerald-400/30 text-emerald-800 dark:text-emerald-200'
                  : 'bg-rose-50 dark:bg-rose-500/15 ring-rose-300/50 dark:ring-rose-400/30 text-rose-800 dark:text-rose-200'
              }`}
              title="Run Invariant #15 (hash-chain integrity)"
            >
              <Icon name={
                verifying ? 'progress_activity' :
                chainStatus == null ? 'lock' :
                chainStatus.ok ? 'verified' : 'gpp_bad'
              } size={14} className={verifying ? 'animate-spin' : ''} />
              {verifying ? 'Verifying…'
                : chainStatus == null ? 'Verify chain'
                : chainStatus.ok ? `Chain intact (${chainStatus.rowsChecked ?? 0} rows)`
                : `Chain BROKEN (${chainStatus.deviation ?? '?'} breaks)`}
            </button>
            )}
          </div>
        </div>
        {/* Detail strip when chain status is set */}
        {view === 'pnl' && chainStatus && !chainStatus.ok && (
          <div className="mt-3 rounded-xl bg-rose-50/70 dark:bg-rose-900/30 ring-1 ring-inset ring-rose-200/60 dark:ring-rose-700/40 p-3 text-[12px] text-rose-800 dark:text-rose-200">
            <div className="font-semibold mb-1">
              <Icon name="gpp_bad" size={14} className="inline mr-1 -mt-0.5" />
              Hash chain integrity FAILED — admin_wallet_ledger may have been tampered with
            </div>
            <div className="opacity-90">{chainStatus.message}</div>
            {chainStatus.sample && chainStatus.sample.length > 0 && (
              <ul className="mt-2 space-y-0.5 text-[11.5px]">
                {chainStatus.sample.slice(0, 5).map(s => (
                  <li key={s.id}>
                    • row #{s.id}: <span className="font-semibold">{s.failure}</span>
                    {s.hint && <span className="opacity-80"> — {s.hint}</span>}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      {/* ═══════════════════════════════════════════════════════════
          P&L VIEW (Operating Wallet)
          ═══════════════════════════════════════════════════════════ */}
      {view === 'pnl' && (
      <>
      {/* ─── Filter bar ────────────────────────────────────────── */}
      <div className="rounded-2xl bg-white/70 dark:bg-arena-800/40 ring-1 ring-inset ring-arena-200/60 dark:ring-arena-700/40 p-4 backdrop-blur-sm">
        <div className="flex items-center justify-between gap-2 mb-3">
          <h3 className="font-bold text-[13px] text-neutral-900 dark:text-white inline-flex items-center gap-1.5">
            <Icon name="filter_alt" size={15} className="text-violet-600 dark:text-violet-300" />
            Filters
          </h3>
          {(kinds.length > 0 || from || to || search || selectedCounterpartyKind || counterpartyId) && (
            <button onClick={clearAllFilters} className="text-[11.5px] font-semibold text-arena-600 dark:text-neon-200/65 hover:text-rose-600 dark:hover:text-rose-300 transition">
              Clear all
            </button>
          )}
        </div>

        {/* Phase 17.3 — Category quick-filters. Each chip selects/clears
            the bundle of kinds in its category. Clicking "Manual transfers"
            here adds manual_credit + manual_debit to the kind selection. */}
        <div className="mb-3">
          <div className="text-[10.5px] uppercase tracking-wider font-bold text-arena-500 dark:text-neon-200/55 mb-1.5">
            Category quick-filter
          </div>
          <div className="flex flex-wrap gap-1.5">
            {PNL_CATEGORIES.map(cat => {
              const tone = CATEGORY_TONE[cat.tone];
              const active = cat.kinds.some(k => kinds.includes(k));
              return (
                <button
                  key={cat.id}
                  onClick={() => toggleCategory(cat.id)}
                  className={`px-2.5 py-1 rounded-full text-[11.5px] font-bold ring-1 ring-inset transition inline-flex items-center gap-1
                    ${active ? tone.active : tone.inactive}`}
                  title={`Toggle: ${cat.kinds.join(', ')}`}
                >
                  <Icon name={cat.icon} size={12} filled={active} />
                  {cat.label}
                  <span className="opacity-70 font-mono text-[10px]">({cat.kinds.length})</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Kind chips */}
        <div className="mb-3">
          <div className="text-[10.5px] uppercase tracking-wider font-bold text-arena-500 dark:text-neon-200/55 mb-1.5">Kind (granular)</div>
          <div className="flex flex-wrap gap-1.5">
            {kindList.length === 0 && <span className="text-[11px] text-arena-500 dark:text-neon-200/60 italic">Loading kinds…</span>}
            {kindList.map(k => {
              const grp = KIND_GROUP[k] || 'other';
              const tone = GROUP_TONE[grp];
              const active = kinds.includes(k);
              return (
                <button
                  key={k}
                  onClick={() => toggleKind(k)}
                  className={`px-2 py-1 rounded-full text-[11px] font-mono ring-1 ring-inset transition ${
                    active
                      ? `${tone.bg} ${tone.ring} ${tone.text} font-semibold shadow-sm`
                      : 'bg-white/60 dark:bg-arena-900/30 ring-arena-200/50 dark:ring-arena-600/40 text-arena-600 dark:text-neon-200/65 hover:bg-arena-50 dark:hover:bg-arena-800/40'
                  }`}
                  title={`${tone.label} — ${k}`}
                >
                  {k}
                </button>
              );
            })}
          </div>
        </div>

        {/* Row 2: counterparty + date + search */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
          {/* Counterparty kind */}
          <div>
            <div className="text-[10.5px] uppercase tracking-wider font-bold text-arena-500 dark:text-neon-200/55 mb-1">Counterparty</div>
            <select
              value={selectedCounterpartyKind}
              onChange={(e) => { setSelectedCounterpartyKind(e.target.value); setPage(0); }}
              className="w-full rounded-lg bg-white dark:bg-arena-900/40 ring-1 ring-inset ring-arena-200/60 dark:ring-arena-600/40 px-2.5 py-1.5 text-[12px] text-arena-700 dark:text-neon-200/85 focus:outline-none focus:ring-2 focus:ring-violet-400">
              <option value="">— any —</option>
              {counterpartyKinds.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          {/* Counterparty id */}
          <div>
            <div className="text-[10.5px] uppercase tracking-wider font-bold text-arena-500 dark:text-neon-200/55 mb-1">Counterparty ID</div>
            <input
              type="number" min={1}
              value={counterpartyId}
              onChange={(e) => { setCounterpartyId(e.target.value); setPage(0); }}
              placeholder="e.g. 74"
              className="w-full rounded-lg bg-white dark:bg-arena-900/40 ring-1 ring-inset ring-arena-200/60 dark:ring-arena-600/40 px-2.5 py-1.5 text-[12px] text-arena-700 dark:text-neon-200/85 font-mono focus:outline-none focus:ring-2 focus:ring-violet-400" />
          </div>
          {/* From */}
          <div>
            <div className="text-[10.5px] uppercase tracking-wider font-bold text-arena-500 dark:text-neon-200/55 mb-1">From</div>
            <input
              type="date"
              value={from}
              onChange={(e) => { setFrom(e.target.value); setPage(0); }}
              className="w-full rounded-lg bg-white dark:bg-arena-900/40 ring-1 ring-inset ring-arena-200/60 dark:ring-arena-600/40 px-2.5 py-1.5 text-[12px] text-arena-700 dark:text-neon-200/85 focus:outline-none focus:ring-2 focus:ring-violet-400" />
          </div>
          {/* To */}
          <div>
            <div className="text-[10.5px] uppercase tracking-wider font-bold text-arena-500 dark:text-neon-200/55 mb-1">To</div>
            <input
              type="date"
              value={to}
              onChange={(e) => { setTo(e.target.value); setPage(0); }}
              className="w-full rounded-lg bg-white dark:bg-arena-900/40 ring-1 ring-inset ring-arena-200/60 dark:ring-arena-600/40 px-2.5 py-1.5 text-[12px] text-arena-700 dark:text-neon-200/85 focus:outline-none focus:ring-2 focus:ring-violet-400" />
          </div>
        </div>

        {/* Row 3: search (full-width) */}
        <div className="mt-2">
          <div className="text-[10.5px] uppercase tracking-wider font-bold text-arena-500 dark:text-neon-200/55 mb-1">Search source_ref / note</div>
          <input
            type="text"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            placeholder="e.g. match-1234, bot_5, manual_correction…"
            className="w-full rounded-lg bg-white dark:bg-arena-900/40 ring-1 ring-inset ring-arena-200/60 dark:ring-arena-600/40 px-2.5 py-1.5 text-[12px] text-arena-700 dark:text-neon-200/85 focus:outline-none focus:ring-2 focus:ring-violet-400" />
        </div>
      </div>

      {/* ─── Error / empty / data table ─────────────────────────── */}
      {err && (
        <div className="rounded-xl bg-rose-50 dark:bg-rose-900/30 ring-1 ring-inset ring-rose-200 dark:ring-rose-700/40 p-3 text-[12px] text-rose-800 dark:text-rose-200">
          {err}
        </div>
      )}

      <div className="rounded-2xl bg-white/70 dark:bg-arena-800/40 ring-1 ring-inset ring-arena-200/60 dark:ring-arena-700/40 backdrop-blur-sm overflow-hidden">
        {loading && (
          <div className="grid place-items-center py-10 text-arena-600 dark:text-neon-200/70 text-[12px]">
            <Icon name="progress_activity" size={20} className="animate-spin" />
            <div className="mt-1.5">Loading ledger…</div>
          </div>
        )}
        {!loading && data && data.rows.length === 0 && (
          <div className="grid place-items-center py-10 text-arena-600 dark:text-neon-200/65 text-[12.5px]">
            <Icon name="inbox" size={24} />
            <div className="mt-1.5 font-semibold">No rows match your filters</div>
            <div className="text-[11px] opacity-80 mt-0.5">Try clearing some filters or widening the date range.</div>
          </div>
        )}
        {!loading && data && data.rows.length > 0 && (
          <>
            {/* Desktop table */}
            <div className="hidden lg:block overflow-x-auto">
              <table className="w-full text-[12px]">
                <thead className="bg-arena-50/60 dark:bg-arena-700/30 text-arena-600 dark:text-neon-200/65">
                  <tr className="text-left">
                    <th className="px-3 py-2 font-semibold">When</th>
                    <th className="px-3 py-2 font-semibold">Kind</th>
                    <th className="px-3 py-2 font-semibold text-right">Amount</th>
                    <th className="px-3 py-2 font-semibold text-right">Balance after</th>
                    <th className="px-3 py-2 font-semibold">Counterparty</th>
                    <th className="px-3 py-2 font-semibold">Source / Match</th>
                    <th className="px-3 py-2 font-semibold">Note</th>
                    <th className="px-3 py-2 font-semibold">Hash</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-arena-100 dark:divide-white/5">
                  {data.rows.map(r => {
                    const grp = KIND_GROUP[r.kind] || 'other';
                    const tone = GROUP_TONE[grp];
                    return (
                      <motion.tr key={r.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                                 className="hover:bg-arena-50/40 dark:hover:bg-arena-700/20 transition">
                        <td className="px-3 py-2 text-arena-700 dark:text-neon-200/85 tabular-nums whitespace-nowrap"
                            title={r.occurred_at}>
                          {fmtWhen(r.occurred_at)}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10.5px] font-mono ring-1 ring-inset ${tone.bg} ${tone.ring} ${tone.text}`}
                                title={`${tone.label}`}>
                            {r.kind}
                          </span>
                        </td>
                        <td className={`px-3 py-2 text-right tabular-nums font-bold whitespace-nowrap ${
                          r.amount > 0 ? 'text-emerald-600 dark:text-emerald-400'
                          : r.amount < 0 ? 'text-rose-600 dark:text-rose-400'
                          : 'text-arena-700 dark:text-neon-200/80'
                        }`}>
                          {r.amount > 0 ? '+' : ''}{inr(r.amount)}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-arena-700 dark:text-neon-200/85 whitespace-nowrap">
                          {inr(r.balance_after)}
                        </td>
                        <td className="px-3 py-2 text-arena-700 dark:text-neon-200/85 whitespace-nowrap">
                          {r.counterparty_kind ? (
                            <span className="font-mono text-[11px]">
                              {r.counterparty_kind}#{r.counterparty_id ?? '—'}
                            </span>
                          ) : <span className="text-arena-400 dark:text-neon-200/70 italic">—</span>}
                        </td>
                        <td className="px-3 py-2 text-arena-700 dark:text-neon-200/85 max-w-[220px]">
                          {r.match_id && (
                            <div className="font-mono text-[10.5px] text-violet-700 dark:text-violet-300 truncate" title={r.match_id}>
                              match: {r.match_id}
                            </div>
                          )}
                          {r.source_ref && (
                            <div className="font-mono text-[10.5px] truncate text-arena-600 dark:text-neon-200/65" title={r.source_ref}>
                              {r.source_ref}
                            </div>
                          )}
                        </td>
                        {/* Phase 17.3 — note rendered prominently. No truncate, no hover-required: the custom message
                            from a manual recharge (AgentDirectOps.js) is the entire reason admin paged into this row,
                            so it must be readable in-place. */}
                        <td className="px-3 py-2 text-arena-800 dark:text-neon-200/90 max-w-[360px] leading-snug">
                          {r.note ? (
                            <div className="text-[12px]">
                              <Icon name="format_quote" size={11} className="inline align-text-bottom mr-1 text-arena-500 dark:text-neon-200/55" />
                              <span className="italic">{r.note}</span>
                            </div>
                          ) : <span className="text-arena-400 dark:text-neon-200/70 italic">—</span>}
                        </td>
                        <td className="px-3 py-2">
                          <span
                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md
                                       bg-arena-50 dark:bg-arena-900/40 ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40
                                       text-[10.5px] font-mono text-arena-700 dark:text-neon-200/85"
                            title={`prev_hash: ${r.prev_hash}\nrow_hash:  ${r.row_hash}`}
                          >
                            <Icon name="lock" size={11} className="text-emerald-600 dark:text-emerald-400" />
                            {shortHash(r.row_hash)}
                          </span>
                        </td>
                      </motion.tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile cards (stacked) */}
            <div className="lg:hidden divide-y divide-arena-100 dark:divide-white/5">
              {data.rows.map(r => {
                const grp = KIND_GROUP[r.kind] || 'other';
                const tone = GROUP_TONE[grp];
                return (
                  <div key={r.id} className="p-3 space-y-1.5">
                    <div className="flex items-center justify-between gap-2">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10.5px] font-mono ring-1 ring-inset ${tone.bg} ${tone.ring} ${tone.text}`}>
                        {r.kind}
                      </span>
                      <span className={`font-bold tabular-nums text-[14px] ${
                        r.amount > 0 ? 'text-emerald-600 dark:text-emerald-400'
                        : r.amount < 0 ? 'text-rose-600 dark:text-rose-400'
                        : 'text-arena-700'
                      }`}>
                        {r.amount > 0 ? '+' : ''}{inr(r.amount)}
                      </span>
                    </div>
                    <div className="text-[11px] text-arena-600 dark:text-neon-200/70" title={r.occurred_at}>
                      {fmtWhen(r.occurred_at)}  ·  balance after: <span className="tabular-nums font-semibold">{inr(r.balance_after)}</span>
                    </div>
                    {r.counterparty_kind && (
                      <div className="text-[11px] text-arena-700 dark:text-neon-200/85 font-mono">
                        cp: {r.counterparty_kind}#{r.counterparty_id ?? '—'}
                      </div>
                    )}
                    {(r.match_id || r.source_ref) && (
                      <div className="text-[10.5px] font-mono text-arena-600 dark:text-neon-200/65 break-all">
                        {r.match_id && <div className="text-violet-700 dark:text-violet-300">match: {r.match_id}</div>}
                        {r.source_ref && <div>{r.source_ref}</div>}
                      </div>
                    )}
                    {r.note && (
                      <div className="text-[11px] text-arena-700 dark:text-neon-200/85">{r.note}</div>
                    )}
                    <div className="flex items-center gap-1.5 text-[10.5px] text-arena-600 dark:text-neon-200/65 pt-0.5">
                      <Icon name="lock" size={11} className="text-emerald-600 dark:text-emerald-400" />
                      <span className="font-mono">{shortHash(r.row_hash)}</span>
                      <span className="opacity-50">·</span>
                      <span>id #{r.id}</span>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Pagination */}
            <div className="px-3 py-2 border-t border-arena-200/60 dark:border-arena-700/40 flex flex-wrap items-center justify-between gap-2 text-[12px] text-arena-700 dark:text-neon-200/85">
              <div>
                Showing <span className="font-semibold">{data.offset + 1}</span>–
                <span className="font-semibold">{Math.min(data.offset + data.rows.length, data.total)}</span> of{' '}
                <span className="font-semibold">{data.total.toLocaleString('en-IN')}</span> rows
              </div>
              <div className="inline-flex items-center gap-1">
                <button
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  disabled={page === 0}
                  className="px-2 py-1 rounded-lg ring-1 ring-inset ring-arena-200/60 dark:ring-arena-600/40 text-[12px] disabled:opacity-40 hover:bg-arena-50 dark:hover:bg-arena-700/40 transition"
                >
                  <Icon name="chevron_left" size={14} />
                </button>
                <span className="px-2 text-[11.5px] text-arena-600 dark:text-neon-200/65 tabular-nums">
                  Page {page + 1} / {pages}
                </span>
                <button
                  onClick={() => setPage(p => Math.min(pages - 1, p + 1))}
                  disabled={page >= pages - 1}
                  className="px-2 py-1 rounded-lg ring-1 ring-inset ring-arena-200/60 dark:ring-arena-600/40 text-[12px] disabled:opacity-40 hover:bg-arena-50 dark:hover:bg-arena-700/40 transition"
                >
                  <Icon name="chevron_right" size={14} />
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Footer provenance */}
      <div className="text-[10.5px] text-arena-500 dark:text-neon-200/45 text-center italic">
        Source: <code>admin_wallet_ledger</code> · hashed via SHA-256 (Migration 084) · Invariant #15 runs nightly at 04:45.
      </div>
      </>
      )}

      {/* ═══════════════════════════════════════════════════════════
          DEPOSIT VIEW (Security-Deposit Liability)
          ═══════════════════════════════════════════════════════════ */}
      {view === 'deposit' && (
        <DepositLedgerView
          rows={depositData?.rows || []}
          total={depositData?.total || 0}
          offset={depositData?.offset || 0}
          limit={depositData?.limit || PAGE}
          loading={depositLoading}
          err={depositErr}
          kinds={depositKinds}
          kindList={depositKindList}
          entryTypes={depositEntryTypes}
          agentId={depositAgentId}
          search={depositSearch}
          from={from}
          to={to}
          page={depositPage}
          onToggleKind={toggleDepositKind}
          onToggleEntryType={toggleDepositEntryType}
          onSetAgentId={(v) => { setDepositAgentId(v); setDepositPage(0); }}
          onSetSearch={(v) => { setDepositSearch(v); setDepositPage(0); }}
          onSetFrom={(v) => { setFrom(v); setDepositPage(0); }}
          onSetTo={(v) => { setTo(v); setDepositPage(0); }}
          onSetPage={setDepositPage}
          onClearAll={clearAllDepositFilters}
        />
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// Phase 17.2 — DepositLedgerView
// ════════════════════════════════════════════════════════════════════
//
// Encapsulated as a sub-component because the columns differ from the
// P&L table (liability_after instead of balance_after, agent_id/agent
// instead of counterparty, entry_type chips instead of kind groups) —
// keeps the parent component readable.

const DEPOSIT_ENTRY_TYPES: Array<{ id: string; label: string; icon: string; tone: 'indigo' | 'amber' | 'sky' | 'fuchsia' | 'yellow' | 'arena' }> = [
  { id: 'manual_admin',          label: 'Manual (admin in)',        icon: 'apartment',    tone: 'indigo' },
  { id: 'manual_admin_withdraw', label: 'Manual (admin out)',       icon: 'apartment',    tone: 'indigo' },
  { id: 'manual_parent',         label: 'Manual (parent)',          icon: 'account_tree', tone: 'amber'  },
  { id: 'request_recharge',      label: 'Request — recharge',       icon: 'inbox',        tone: 'sky'    },
  { id: 'request_withdrawal',    label: 'Request — withdrawal',     icon: 'inbox',        tone: 'sky'    },
  { id: 'exit_refund',           label: 'Exit refund',              icon: 'logout',       tone: 'fuchsia'},
  { id: 'settlement_burn',       label: 'Settlement burn',          icon: 'sync',         tone: 'fuchsia'},
  { id: 'correction',            label: 'Correction',               icon: 'tune',         tone: 'yellow' },
  { id: 'system',                label: 'System',                   icon: 'auto_awesome', tone: 'arena'  },
];

const ENTRY_TONE: Record<string, { active: string; inactive: string }> = {
  indigo:  { active: 'bg-indigo-600 text-white ring-indigo-600',     inactive: 'bg-indigo-50 dark:bg-indigo-500/15 ring-indigo-300/40 dark:ring-indigo-400/30 text-indigo-800 dark:text-indigo-200 hover:bg-indigo-100' },
  amber:   { active: 'bg-amber-600 text-white ring-amber-600',       inactive: 'bg-amber-50 dark:bg-amber-500/15 ring-amber-300/40 dark:ring-amber-400/30 text-amber-800 dark:text-amber-200 hover:bg-amber-100' },
  sky:     { active: 'bg-sky-600 text-white ring-sky-600',           inactive: 'bg-sky-50 dark:bg-sky-500/15 ring-sky-300/40 dark:ring-sky-400/30 text-sky-800 dark:text-sky-200 hover:bg-sky-100' },
  fuchsia: { active: 'bg-fuchsia-600 text-white ring-fuchsia-600',   inactive: 'bg-fuchsia-50 dark:bg-fuchsia-500/15 ring-fuchsia-300/40 dark:ring-fuchsia-400/30 text-fuchsia-800 dark:text-fuchsia-200 hover:bg-fuchsia-100' },
  yellow:  { active: 'bg-yellow-600 text-white ring-yellow-600',     inactive: 'bg-yellow-50 dark:bg-yellow-500/15 ring-yellow-300/40 dark:ring-yellow-400/30 text-yellow-800 dark:text-yellow-200 hover:bg-yellow-100' },
  arena:   { active: 'bg-slate-700 text-white ring-slate-700',       inactive: 'bg-arena-100 dark:bg-arena-700/40 ring-arena-300/40 dark:ring-arena-600/50 text-arena-700 dark:text-neon-200/85 hover:bg-arena-200' },
};

function DepositLedgerView(props: {
  rows: DepositLedgerRow[];
  total: number; offset: number; limit: number;
  loading: boolean; err: string | null;
  kinds: string[]; kindList: string[];
  entryTypes: string[];
  agentId: string; search: string;
  from: string; to: string;
  page: number;
  onToggleKind: (k: string) => void;
  onToggleEntryType: (t: string) => void;
  onSetAgentId: (v: string) => void;
  onSetSearch: (v: string) => void;
  onSetFrom: (v: string) => void;
  onSetTo:   (v: string) => void;
  onSetPage: (n: number) => void;
  onClearAll: () => void;
}) {
  const { rows, total, offset, limit, loading, err,
    kinds, kindList, entryTypes, agentId, search, from, to, page,
    onToggleKind, onToggleEntryType, onSetAgentId, onSetSearch,
    onSetFrom, onSetTo, onSetPage, onClearAll } = props;
  const pages = Math.max(1, Math.ceil(total / limit));
  const hasFilters = kinds.length > 0 || entryTypes.length > 0 || agentId || search || from || to;
  return (
    <>
      {/* Filter bar */}
      <div className="rounded-2xl bg-white/70 dark:bg-arena-800/40 ring-1 ring-inset ring-emerald-200/60 dark:ring-emerald-700/40 p-4 backdrop-blur-sm">
        <div className="flex items-center justify-between gap-2 mb-3">
          <h3 className="font-bold text-[13px] text-neutral-900 dark:text-white inline-flex items-center gap-1.5">
            <Icon name="filter_alt" size={15} className="text-emerald-600 dark:text-emerald-300" />
            Filters
          </h3>
          {hasFilters && (
            <button onClick={onClearAll} className="text-[11.5px] font-semibold text-arena-600 dark:text-neon-200/65 hover:text-rose-600 dark:hover:text-rose-300 transition">
              Clear all
            </button>
          )}
        </div>

        {/* Entry-type chips (the most-used filter for this view) */}
        <div className="mb-3">
          <div className="text-[10.5px] uppercase tracking-wider font-bold text-arena-500 dark:text-neon-200/55 mb-1.5">
            Entry type
          </div>
          <div className="flex flex-wrap gap-1.5">
            {DEPOSIT_ENTRY_TYPES.map(et => {
              const active = entryTypes.includes(et.id);
              const tone = ENTRY_TONE[et.tone];
              return (
                <button
                  key={et.id}
                  onClick={() => onToggleEntryType(et.id)}
                  className={`px-2.5 py-1 rounded-full text-[11.5px] font-bold ring-1 ring-inset transition inline-flex items-center gap-1
                    ${active ? tone.active : tone.inactive}`}
                >
                  <Icon name={et.icon} size={12} filled={active} />
                  {et.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Kind chips (granular DB enum) */}
        <div className="mb-3">
          <div className="text-[10.5px] uppercase tracking-wider font-bold text-arena-500 dark:text-neon-200/55 mb-1.5">Kind (DB enum)</div>
          <div className="flex flex-wrap gap-1.5">
            {kindList.length === 0 && <span className="text-[11px] text-arena-500 dark:text-neon-200/60 italic">Loading kinds…</span>}
            {kindList.map(k => {
              const active = kinds.includes(k);
              return (
                <button
                  key={k}
                  onClick={() => onToggleKind(k)}
                  className={`px-2 py-1 rounded-full text-[11px] font-mono ring-1 ring-inset transition ${
                    active
                      ? 'bg-emerald-600 text-white ring-emerald-600 font-semibold shadow-sm'
                      : 'bg-white/60 dark:bg-arena-900/30 ring-arena-200/50 dark:ring-arena-600/40 text-arena-600 dark:text-neon-200/65 hover:bg-arena-50 dark:hover:bg-arena-800/40'
                  }`}
                >
                  {k}
                </button>
              );
            })}
          </div>
        </div>

        {/* Row 2: agent + date + search */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
          <div>
            <div className="text-[10.5px] uppercase tracking-wider font-bold text-arena-500 dark:text-neon-200/55 mb-1">Agent ID</div>
            <input
              type="number" min={1}
              value={agentId}
              onChange={(e) => onSetAgentId(e.target.value)}
              placeholder="e.g. 1 (L1Agent)"
              className="w-full rounded-lg bg-white dark:bg-arena-900/40 ring-1 ring-inset ring-arena-200/60 dark:ring-arena-600/40 px-2.5 py-1.5 text-[12px] text-arena-700 dark:text-neon-200/85 font-mono focus:outline-none focus:ring-2 focus:ring-emerald-400" />
          </div>
          <div>
            <div className="text-[10.5px] uppercase tracking-wider font-bold text-arena-500 dark:text-neon-200/55 mb-1">From</div>
            <input type="date" value={from} onChange={(e) => onSetFrom(e.target.value)}
                   className="w-full rounded-lg bg-white dark:bg-arena-900/40 ring-1 ring-inset ring-arena-200/60 dark:ring-arena-600/40 px-2.5 py-1.5 text-[12px] text-arena-700 dark:text-neon-200/85 focus:outline-none focus:ring-2 focus:ring-emerald-400" />
          </div>
          <div>
            <div className="text-[10.5px] uppercase tracking-wider font-bold text-arena-500 dark:text-neon-200/55 mb-1">To</div>
            <input type="date" value={to} onChange={(e) => onSetTo(e.target.value)}
                   className="w-full rounded-lg bg-white dark:bg-arena-900/40 ring-1 ring-inset ring-arena-200/60 dark:ring-arena-600/40 px-2.5 py-1.5 text-[12px] text-arena-700 dark:text-neon-200/85 focus:outline-none focus:ring-2 focus:ring-emerald-400" />
          </div>
          <div>
            <div className="text-[10.5px] uppercase tracking-wider font-bold text-arena-500 dark:text-neon-200/55 mb-1">Search note / ref</div>
            <input type="text" value={search} onChange={(e) => onSetSearch(e.target.value)}
                   placeholder="cash collected, 4291, admin-credit-deposit#1…"
                   className="w-full rounded-lg bg-white dark:bg-arena-900/40 ring-1 ring-inset ring-arena-200/60 dark:ring-arena-600/40 px-2.5 py-1.5 text-[12px] text-arena-700 dark:text-neon-200/85 focus:outline-none focus:ring-2 focus:ring-emerald-400" />
          </div>
        </div>
      </div>

      {/* Error / empty / data */}
      {err && (
        <div className="rounded-xl bg-rose-50 dark:bg-rose-900/30 ring-1 ring-inset ring-rose-200 dark:ring-rose-700/40 p-3 text-[12px] text-rose-800 dark:text-rose-200">
          {err}
        </div>
      )}
      <div className="rounded-2xl bg-white/70 dark:bg-arena-800/40 ring-1 ring-inset ring-arena-200/60 dark:ring-arena-700/40 backdrop-blur-sm overflow-hidden">
        {loading && (
          <div className="grid place-items-center py-10 text-arena-600 dark:text-neon-200/70 text-[12px]">
            <Icon name="progress_activity" size={20} className="animate-spin" />
            <div className="mt-1.5">Loading deposit ledger…</div>
          </div>
        )}
        {!loading && rows.length === 0 && (
          <div className="grid place-items-center py-10 text-arena-600 dark:text-neon-200/65 text-[12.5px]">
            <Icon name="inbox" size={24} />
            <div className="mt-1.5 font-semibold">No rows match your filters</div>
            <div className="text-[11px] opacity-80 mt-0.5">Try clearing some filters or switching to the P&L view.</div>
          </div>
        )}
        {!loading && rows.length > 0 && (
          <>
            {/* Desktop table */}
            <div className="hidden lg:block overflow-x-auto">
              <table className="w-full text-[12px]">
                <thead className="bg-emerald-50/60 dark:bg-emerald-700/30 text-arena-600 dark:text-neon-200/65">
                  <tr className="text-left">
                    <th className="px-3 py-2 font-semibold">When</th>
                    <th className="px-3 py-2 font-semibold">Entry</th>
                    <th className="px-3 py-2 font-semibold text-right">Amount</th>
                    <th className="px-3 py-2 font-semibold text-right">Liability after</th>
                    <th className="px-3 py-2 font-semibold">Agent</th>
                    <th className="px-3 py-2 font-semibold">Note</th>
                    <th className="px-3 py-2 font-semibold">Ref</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-arena-100 dark:divide-white/5">
                  {rows.map((r) => {
                    const entryDef = DEPOSIT_ENTRY_TYPES.find(x => x.id === r.entry_type) || DEPOSIT_ENTRY_TYPES[DEPOSIT_ENTRY_TYPES.length - 1];
                    const tone = ENTRY_TONE[entryDef.tone];
                    return (
                      <motion.tr key={r.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                                 className="hover:bg-arena-50/40 dark:hover:bg-arena-700/20 transition">
                        <td className="px-3 py-2 text-arena-700 dark:text-neon-200/85 tabular-nums whitespace-nowrap" title={r.occurred_at}>
                          {fmtWhen(r.occurred_at)}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] ring-1 ring-inset font-bold ${tone.inactive}`}>
                            <Icon name={entryDef.icon} size={11} />
                            {entryDef.label}
                          </span>
                          <div className="text-[9.5px] font-mono text-arena-500 dark:text-neon-200/50 mt-0.5">{r.kind}</div>
                        </td>
                        <td className={`px-3 py-2 text-right tabular-nums font-bold whitespace-nowrap
                          ${r.amount > 0 ? 'text-emerald-600 dark:text-emerald-400'
                          : r.amount < 0 ? 'text-rose-600 dark:text-rose-400' : 'text-arena-700 dark:text-neon-200/80'}`}>
                          {r.amount > 0 ? '+' : ''}{inr(r.amount)}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-arena-700 dark:text-neon-200/85 whitespace-nowrap">
                          {inr(r.liability_after)}
                        </td>
                        <td className="px-3 py-2 text-arena-700 dark:text-neon-200/85 whitespace-nowrap">
                          {r.agent ? (
                            <div className="flex flex-col">
                              <span className="font-semibold">@{r.agent.username}</span>
                              <span className="text-[10px] text-arena-500 dark:text-neon-200/55">
                                {r.agent.level != null && `${levelName(r.agent.level)} · `}#{r.agent.id}
                                {r.agent.parent_username && <> · under @{r.agent.parent_username}</>}
                              </span>
                            </div>
                          ) : (
                            r.agent_id != null ? <span className="font-mono text-[11px]">#{r.agent_id}</span> : <span className="text-arena-400 dark:text-neon-200/70 italic">—</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-arena-800 dark:text-neon-200/90 max-w-[320px]">
                          {r.note ? (
                            <div className="text-[12px] leading-snug">
                              <Icon name="format_quote" size={11} className="inline align-text-bottom mr-1 text-arena-500 dark:text-neon-200/55" />
                              <span className="italic">{r.note}</span>
                            </div>
                          ) : <span className="text-arena-400 dark:text-neon-200/70 italic">—</span>}
                        </td>
                        <td className="px-3 py-2 text-arena-600 dark:text-neon-200/70 max-w-[180px]">
                          {r.source_ref && (
                            <div className="font-mono text-[10.5px] truncate" title={r.source_ref}>{r.source_ref}</div>
                          )}
                        </td>
                      </motion.tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {/* Mobile cards */}
            <div className="lg:hidden divide-y divide-arena-100 dark:divide-white/5">
              {rows.map((r) => {
                const entryDef = DEPOSIT_ENTRY_TYPES.find(x => x.id === r.entry_type) || DEPOSIT_ENTRY_TYPES[DEPOSIT_ENTRY_TYPES.length - 1];
                const tone = ENTRY_TONE[entryDef.tone];
                return (
                  <div key={r.id} className="p-3 space-y-1.5">
                    <div className="flex items-center justify-between gap-2">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] ring-1 ring-inset font-bold ${tone.inactive}`}>
                        <Icon name={entryDef.icon} size={11} />
                        {entryDef.label}
                      </span>
                      <span className={`font-bold tabular-nums text-[14px]
                        ${r.amount > 0 ? 'text-emerald-600 dark:text-emerald-400' : r.amount < 0 ? 'text-rose-600 dark:text-rose-400' : 'text-arena-700'}`}>
                        {r.amount > 0 ? '+' : ''}{inr(r.amount)}
                      </span>
                    </div>
                    <div className="text-[11px] text-arena-600 dark:text-neon-200/70" title={r.occurred_at}>
                      {fmtWhen(r.occurred_at)} · liability after: <span className="tabular-nums font-semibold">{inr(r.liability_after)}</span>
                    </div>
                    {r.agent && (
                      <div className="text-[11px] text-arena-700 dark:text-neon-200/85">
                        Agent: <b>@{r.agent.username}</b> {r.agent.level != null && `(${levelName(r.agent.level)})`}
                        {r.agent.parent_username && <span className="text-arena-500 dark:text-neon-200/60"> · under @{r.agent.parent_username}</span>}
                      </div>
                    )}
                    {r.note && (
                      <div className="rounded-md bg-arena-50/70 dark:bg-arena-700/40 ring-1 ring-inset ring-arena-200/55 dark:ring-arena-600/40 px-2 py-1 text-[11.5px] italic text-arena-800 dark:text-neon-200/90">
                        <Icon name="format_quote" size={10} className="inline align-text-bottom mr-1 text-arena-500 dark:text-neon-200/55" />
                        {r.note}
                      </div>
                    )}
                    {r.source_ref && (
                      <div className="text-[10px] font-mono text-arena-500 dark:text-neon-200/55 truncate" title={r.source_ref}>
                        ref: {r.source_ref}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            {/* Pagination */}
            <div className="px-3 py-2 border-t border-arena-200/60 dark:border-arena-700/40 flex flex-wrap items-center justify-between gap-2 text-[12px] text-arena-700 dark:text-neon-200/85">
              <div>
                Showing <span className="font-semibold">{offset + 1}</span>–
                <span className="font-semibold">{Math.min(offset + rows.length, total)}</span> of{' '}
                <span className="font-semibold">{total.toLocaleString('en-IN')}</span> rows
              </div>
              <div className="inline-flex items-center gap-1">
                <button onClick={() => onSetPage(Math.max(0, page - 1))} disabled={page === 0}
                  className="px-2 py-1 rounded-lg ring-1 ring-inset ring-arena-200/60 dark:ring-arena-600/40 text-[12px] disabled:opacity-40 hover:bg-arena-50 dark:hover:bg-arena-700/40 transition">
                  <Icon name="chevron_left" size={14} />
                </button>
                <span className="px-2 text-[11.5px] text-arena-600 dark:text-neon-200/65 tabular-nums">
                  Page {page + 1} / {pages}
                </span>
                <button onClick={() => onSetPage(Math.min(pages - 1, page + 1))} disabled={page >= pages - 1}
                  className="px-2 py-1 rounded-lg ring-1 ring-inset ring-arena-200/60 dark:ring-arena-600/40 text-[12px] disabled:opacity-40 hover:bg-arena-50 dark:hover:bg-arena-700/40 transition">
                  <Icon name="chevron_right" size={14} />
                </button>
              </div>
            </div>
          </>
        )}
      </div>
      <div className="text-[10.5px] text-arena-500 dark:text-neon-200/45 text-center italic">
        Source: <code>admin_deposit_ledger</code> · Super-Master-only mirror of <code>agent_deposit_ledger</code> · reconciliation invariant runs nightly.
      </div>
    </>
  );
}
